import { FetchResult } from '@apollo/client';
import { zodResolver } from '@hookform/resolvers/zod';
import { FormErrorMessage } from '@pendwa/common-browser/components/Form/ErrorMessage/index.js';
import { FormInputText } from '@pendwa/common-browser/components/Form/InputText/index.js';
import { FormSubmitButton } from '@pendwa/common-browser/components/Form/SubmitButton/index.js';
import { LayoutSpacer } from '@pendwa/common-browser/components/LayoutSpacer/index.js';
import { LayoutSpacerSmall } from '@pendwa/common-browser/components/LayoutSpacerSmall/index.js';
import { extractGraphQLErrorMessage } from '@pendwa/common-browser/core/apollo/utils.js';
import React from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { HooksAccountLoginCodeMutation } from '../../../generated/graphql.js';
import { useAccountLoginCodeGraphql } from '../../../hooks/user/loginCode.js';

const schema = z
  .object({
    code: z.coerce.number(),
  })
  .required();

export const LoginCode: React.FC<{
  message: string;
  backToSendCode: () => void;
  handleSuccess: (result: FetchResult<HooksAccountLoginCodeMutation>) => void;
}> = (props) => {
  const { result: mutation_result, doAction } = useAccountLoginCodeGraphql();

  const {
    handleSubmit,
    register,
    formState: { errors },
  } = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
  });

  return (
    <LayoutSpacer>
      <div className="flex flex-col items-center justify-center">
        <p className="text-center text-sm text-gray-600">{props.message}</p>
      </div>

      {mutation_result.error && (
        <FormErrorMessage
          message={extractGraphQLErrorMessage(mutation_result.error) ?? 'Something went wrong.'}
        />
      )}

      <form
        onSubmit={handleSubmit(async (data) => {
          const action_result = await doAction({
            variables: {
              body: {
                code: data.code,
              },
            },
          });

          if (!action_result.errors && action_result.data?.accountLoginCode?.data?.uuid) {
            setTimeout(() => props.handleSuccess(action_result), 500);
          }
        })}
      >
        <LayoutSpacerSmall>
          <FormInputText
            label={'Code'}
            description={'Enter the code that you received.'}
            input_type={'text'}
            input_name={'code'}
            register={register}
            error={errors.code?.message}
          />

          <FormSubmitButton label={'Sign In'} loading={mutation_result.loading} />
        </LayoutSpacerSmall>
      </form>

      <div className="flex flex-col items-center justify-center">
        <p className="text-center text-sm text-gray-600">Didn't see the code?</p>
        <div className="text-sm">
          <a
            onClick={(event) => {
              event.preventDefault();
              event.stopPropagation();
              props.backToSendCode();
            }}
            className="font-medium text-indigo-600 hover:cursor-pointer hover:text-indigo-500"
          >
            Check email and resend
          </a>
        </div>
      </div>
    </LayoutSpacer>
  );
};
